import React, { useEffect, useState } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useLang } from "../../contexts/LanguageContext";
import { LayoutDashboard, FileStack, ImageIcon, Mail, MailCheck, LogOut, Shield } from "lucide-react";

export default function AdminShell() {
  const { user, loading, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    if (!loading && !user) nav("/admin/login");
  }, [user, loading, nav]);

  if (loading || !user) {
    return <div className="py-32 text-center text-slate500">{t.common.loading}</div>;
  }

  const items = [
    { to: "/admin", icon: LayoutDashboard, label: t.admin.dashboard, end: true },
    { to: "/admin/articles", icon: FileStack, label: t.admin.articles },
    { to: "/admin/media", icon: ImageIcon, label: t.admin.media },
    { to: "/admin/inbox", icon: Mail, label: t.admin.contact_inbox },
    { to: "/admin/newsletter", icon: MailCheck, label: t.admin.newsletter },
  ];

  return (
    <div className="grid lg:grid-cols-[260px_1fr] min-h-[calc(100vh-200px)]" data-testid="admin-shell">
      <aside className="bg-paper text-ink p-6 border-r border-slate300">
        <div className="flex items-center gap-2 mb-8">
          <Shield className="w-4 h-4" />
          <div className="label-eyebrow text-slate600">Admin</div>
        </div>
        <div className="text-[13px] text-ink mb-8 leading-tight">
          <div className="font-semibold">{user.name}</div>
          <div className="text-slate500 text-[11px] mt-1">{user.email}</div>
        </div>
        <nav className="grid gap-1">
          {items.map((it) => {
            const Icon = it.icon;
            const active = it.end ? loc.pathname === it.to : loc.pathname.startsWith(it.to);
            return (
              <Link
                key={it.to}
                to={it.to}
                data-testid={`admin-nav-${it.to.split("/").pop() || "dashboard"}`}
                className={`flex items-center gap-3 h-10 px-3 text-[13px] transition-colors ${
                  active ? "bg-ink text-white" : "text-slate700 hover:bg-white"
                }`}
              >
                <Icon className="w-4 h-4" /> {it.label}
              </Link>
            );
          })}
          <button
            onClick={async () => { await logout(); nav("/admin/login"); }}
            data-testid="admin-logout-btn"
            className="flex items-center gap-3 h-10 px-3 text-[13px] text-slate700 hover:bg-white mt-6 text-left"
          >
            <LogOut className="w-4 h-4" /> {t.admin.logout}
          </button>
        </nav>
      </aside>
      <section className="p-7 lg:p-12 bg-slate50">
        <Outlet />
      </section>
    </div>
  );
}
